import * as React from "react"
import { Container, Row, Col, Card, Button, Badge } from "react-bootstrap"
import { Github, Linkedin, Envelope, Code, Server, Palette, Phone } from "react-bootstrap-icons"
import { useTranslation } from "react-i18next"

export const Home2 = () => {
    const { t } = useTranslation()

    const skills = {
        frontend: ["React", "TypeScript", "JavaScript", "Redux", "Bootstrap", "Webpack", "HTML5", "CSS3"],
        backend: ["C#", ".NET Core", "C++", "ASP.NET", "Entity Framework", "RESTful APIs", "WebSockets"],
        mobile: ["Android", "Kotlin", "Java"],
        tools: ["Git", "Azure", "Visual Studio", "Jira", "Confluence", "Bitbucket", "DevOps", "CI/CD"]
    }

    const projects = [
        {
            title: "TeamViewer Management Console",
            description: "Enterprise web application for device and user management. Full-stack development with focus on security, scalability and cross-platform compatibility.",
            tech: ["C#", "C++", "TypeScript", "React", "Azure"],
            highlight: true
        },
        {
            title: "webapi.teamviewer.com - REST API",
            description: "Lead developer for TeamViewer's REST API. Designed and implemented core API endpoints for enterprise customer integration.",
            tech: ["C#", "REST", "Azure", "DevOps"],
            highlight: false
        },
        {
            title: "User Roles & Permissions System",
            description: "Cross-platform role-based access control system. Lead developer responsible for architecture and implementation across all TeamViewer platforms.",
            tech: ["C#", "C++", "TypeScript", "Security"],
            highlight: false
        },
        {
            title: "Conditional Access & Reach API",
            description: "Security-critical features including conditional access for remote control and unattended remote control API for enterprise customers.",
            tech: ["C#", "C++", "Security", "Enterprise"],
            highlight: false
        }
    ]

    return (
        <div>
   
            <Container fluid className="py-5 bg-secondary text-white">
                <Container>
                    <Row className="align-items-center">
                        <Col lg={8}>
                            <h1 className="display-4 fw-bold mb-3">Senior Software Engineer</h1>
                            <p className="lead mb-4">
                                12+ years at TeamViewer building enterprise-grade solutions.
                                Full-stack expertise in C#, C++, TypeScript, and React with focus on security and scalability.
                            </p>
                            <div className="d-flex gap-3">
                                <Button variant="light" size="lg" className="d-flex align-items-center gap-2">
                                    <Envelope /> Contact Me
                                </Button>
                                <Button variant="outline-light" size="lg" className="d-flex align-items-center gap-2">
                                    <Github /> View Projects
                                </Button>
                            </div>
                        </Col>
                        <Col lg={4} className="text-center mt-4 mt-lg-0">
                            <div
                                style={{
                                    width: "250px",
                                    height: "250px",
                                    borderRadius: "50%",
                                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                                    margin: "0 auto",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    border: "4px solid rgba(255, 255, 255, 0.3)"
                                }}
                            >
                                <Code size={100} />
                            </div>
                        </Col>
                    </Row>
                </Container>
            </Container>

            <Container className="py-5">
                <h2 className="text-center mb-5">Technical Skills</h2>
                <Row className="g-4">
                    <Col md={6} lg={3}>
                        <Card className="h-100 shadow-sm">
                            <Card.Body className="text-center">
                                <Palette size={48} className="text-primary mb-3" />
                                <Card.Title>Frontend</Card.Title>
                                <div className="d-flex flex-wrap gap-2 justify-content-center mt-3">
                                    {skills.frontend.map((skill, idx) => (
                                        <Badge key={idx} bg="secondary" pill>{skill}</Badge>
                                    ))}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={6} lg={3}>
                        <Card className="h-100 shadow-sm">
                            <Card.Body className="text-center">
                                <Server size={48} className="text-success mb-3" />
                                <Card.Title>Backend</Card.Title>
                                <div className="d-flex flex-wrap gap-2 justify-content-center mt-3">
                                    {skills.backend.map((skill, idx) => (
                                        <Badge key={idx} bg="success" pill>{skill}</Badge>
                                    ))}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={6} lg={3}>
                        <Card className="h-100 shadow-sm">
                            <Card.Body className="text-center">
                                <Phone size={48} className="text-info mb-3" />
                                <Card.Title>Mobile</Card.Title>
                                <div className="d-flex flex-wrap gap-2 justify-content-center mt-3">
                                    {skills.mobile.map((skill, idx) => (
                                        <Badge key={idx} bg="info" pill>{skill}</Badge>
                                    ))}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={6} lg={3}>
                        <Card className="h-100 shadow-sm">
                            <Card.Body className="text-center">
                                <Code size={48} className="text-warning mb-3" />
                                <Card.Title>Tools & DevOps</Card.Title>
                                <div className="d-flex flex-wrap gap-2 justify-content-center mt-3">
                                    {skills.tools.map((skill, idx) => (
                                        <Badge key={idx} bg="warning" pill>{skill}</Badge>
                                    ))}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>


            <Container className="py-5">
                <h2 className="text-center mb-5">Featured Projects</h2>
                <Row className="g-4">
                    {projects.map((project, idx) => (
                        <Col key={idx} md={6} lg={project.highlight ? 12 : 6}>
                            <Card className={`h-100 shadow-sm ${project.highlight ? "border-primary" : ""}`}>
                                {project.highlight && (
                                    <Card.Header className="bg-secondary text-white">
                                        <strong>Featured Project</strong>
                                    </Card.Header>
                                )}
                                <Card.Body>
                                    <Card.Title>{project.title}</Card.Title>
                                    <Card.Text className="text-muted">
                                        {project.description}
                                    </Card.Text>
                                    <div className="d-flex flex-wrap gap-2 mb-3">
                                        {project.tech.map((tech, techIdx) => (
                                            <Badge key={techIdx} bg="secondary">{tech}</Badge>
                                        ))}
                                    </div>
                                    <Button variant="outline-primary" size="sm">
                                        View Details
                                    </Button>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </Container>

   
            <Container className="py-5">
                <Row className="justify-content-center">
                    <Col lg={8}>
                        <Card className="shadow">
                            <Card.Body className="p-5">
                                <h2 className="mb-4">About Me</h2>
                                <p className="lead">
                                    Senior Software Engineer at TeamViewer since 2012, with a diploma in Computer Science
                                    from WWU Münster. I approach programming as a problem-solving tool, not an end in itself.
                                </p>
                                <p>
                                    My expertise lies in balancing quality and development time, designing multiple solution
                                    concepts based on available resources, and providing stakeholders with clear decision-making
                                    foundations. I specialize in cross-platform development, security-critical features, and
                                    enterprise-grade REST APIs.
                                </p>
                                <p>
                                    I lead projects by breaking them down into tasks suitable for both junior and senior developers,
                                    mentor team members through code reviews, and believe that good planning and documentation are
                                    essential even in agile development.
                                </p>
                                <p className="mb-4">
                                    My experience spans backend (C#, C++), frontend (TypeScript, React), mobile (Android, Kotlin),
                                    DevOps (Azure, CI/CD), and direct enterprise customer contact.
                                </p>
                                <div className="d-flex gap-3">
                                    <h5 className="mb-0">Connect:</h5>
                                    <a href="#" className="text-decoration-none">
                                        <Github size={24} className="text-dark" />
                                    </a>
                                    <a href="#" className="text-decoration-none">
                                        <Linkedin size={24} className="text-primary" />
                                    </a>
                                    <a href="#" className="text-decoration-none">
                                        <Envelope size={24} className="text-danger" />
                                    </a>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>


            <Container fluid className="py-5 bg-dark text-white">
                <Container className="text-center">
                    <h2 className="mb-3">Pragmatic Problem Solving</h2>
                    <p className="lead mb-4">
                        Programming is a tool. Good planning and documentation matter. There is no perfect solution.
                    </p>
                    <Button variant="secondary" size="lg" className="d-inline-flex align-items-center gap-2">
                        <Envelope /> Get In Touch
                    </Button>
                </Container>
            </Container>
        </div>
    )
}
